<script setup lang="ts">
import { computed, ref } from 'vue';
import { useBrokerLoanAppProcess } from '@/helpers/useBrokerLoanAppProcess';
import { useApplicationApi } from '@/helpers/useApplicationApi';
import { useFeatureFlags } from '@/helpers/useFeatureFlags';
import { DraftEventName } from '@/helpers/draftEvents';
import type { LoanTypeEnum } from '@/types';
import type { IconName } from '@/helpers/icons';
import { LoanType } from '@/helpers/const';
import { ToolTip } from '@/helpers/tippy';
import { RadioGroup, RadioGroupDescription, RadioGroupLabel, RadioGroupOption } from '@headlessui/vue';

const { isPurchaseOnlyLoanTypeEnabled } = useFeatureFlags();

const singleSecurityDescription = 'Unlock equity, sell later: help your clients access their property equity.';
const singleSecurityDescriptionExtended =
  'Unlock equity, sell later: Help your clients access their property equity. Purchase & Pivot: Finance a new acquisition now and exit on their terms during the loan';

const loanTypes = computed(() => [
  {
    id: LoanType.BRIDGING,
    title: 'Bridging Loan',
    icon: 'buyNow' as IconName,
    description: 'Buy now, sell later:  help your clients purchase their next property before selling.',
    tooltip:
      'We take security over both properties so that you can buy before you sell. You will need the funds to pay for the deposit.',
  },
  {
    id: LoanType.SINGLE_SECURITY,
    title: 'Single Security Bridging Loan',
    icon: 'buyNow' as IconName,
    description: isPurchaseOnlyLoanTypeEnabled() ? singleSecurityDescriptionExtended : singleSecurityDescription,
    tooltip:
      'We take first mortgage security over the property being sold only, releasing the funds for the required loan purpose. ',
  },
]);

const { state, loanApp, saveAndGoNext } = useBrokerLoanAppProcess();
const { saveApplicationToDraft } = useApplicationApi();
const selectedLoanType = ref<LoanTypeEnum | undefined>(loanApp.value.loanType || LoanType.BRIDGING);
const isLoading = ref(false);

/**
 * Proceeds to the next step of the application. Persists an initial draft so
 * the back-end can log a start_application event before any property/loan
 * details have been captured.
 */
const startApplication = async (): Promise<void> => {
  if (!selectedLoanType.value || isLoading.value) return;
  isLoading.value = true;
  const incomingProperties = selectedLoanType.value === LoanType.EQUITY_RELEASE ? [] : loanApp.value.incomingProperties;
  const nextLoanApp = { ...loanApp.value, loanType: selectedLoanType.value, incomingProperties };
  let applicationDraftId = loanApp.value.applicationDraftId;
  if (nextLoanApp.brokerId) {
    try {
      applicationDraftId = await saveApplicationToDraft(
        state.value.context,
        nextLoanApp,
        DraftEventName.StartApplication,
      );
    } catch (error) {
      console.error('Failed to save draft on start_application:', error);
    }
  }
  isLoading.value = false;
  saveAndGoNext({ loanType: selectedLoanType.value, incomingProperties, applicationDraftId });
};
</script>

<template>
  <div>
    <div class="mb-4 pb-2">
      <h2 class="text-2xl font-medium">Bridgit <BxHighlight>application</BxHighlight></h2>
      <p class="mt-2 text-gray-600">Select an option below to start.</p>
      <p class="text-gray-600">
        (i) If you’ve already workshopped a scenario, you can use the one-click application to pre-fill your details via
        your email.
      </p>
    </div>

    <RadioGroup v-model="selectedLoanType">
      <div class="mt-6 flex flex-1 flex-wrap gap-4">
        <RadioGroupOption
          v-for="loanType in loanTypes"
          v-slot="{ checked, active }"
          :key="loanType.id"
          :value="loanType.id"
          class="radio-option hover:border-bridgit-vibrantGreen"
          as="template"
          ><div>
            <ToolTip :options="{ placement: 'bottom', delay: 100, touch: false }">
              <div
                :class="[
                  checked ? 'border-transparent' : 'border-gray-300',
                  active
                    ? 'border-bridgit-vibrantGreen ring-2 ring-bridgit-vibrantGreen'
                    : 'hover:border-bridgit-lightGreen hover:ring-2 hover:ring-bridgit-lightGreen',
                  'relative flex h-full cursor-pointer rounded-lg border bg-white p-4 shadow-sm focus:outline-none ',
                ]"
              >
                <span class="flex gap-4">
                  <div class="basis-12 pt-2 text-bridgit-vibrantGreen">
                    <BxIcon :name="loanType.icon" />
                  </div>
                  <span class="flex flex-grow flex-col">
                    <RadioGroupLabel as="span" class="mb-2 block text-sm font-medium text-gray-900">
                      {{ loanType.title }}
                    </RadioGroupLabel>
                    <RadioGroupDescription as="span" class="mt-1 flex items-center text-sm text-gray-500">
                      {{ loanType.description }}
                    </RadioGroupDescription>
                  </span>
                </span>
                <span
                  :class="[
                    active ? 'border' : 'border-2',
                    checked ? 'border-bridgit-vibrantGreen' : 'border-transparent',
                    'pointer-events-none absolute -inset-px rounded-lg',
                  ]"
                  aria-hidden="true"
                />
              </div>
              <template #content> {{ loanType.tooltip }} </template>
            </ToolTip>
          </div>
        </RadioGroupOption>
      </div>
    </RadioGroup>

    <div class="relative my-8 ml-4 min-h-[4rem] leading-7">
      <Transition name="fade">
        <div v-if="selectedLoanType === LoanType.BRIDGING">
          <p>Below is what you'll need to complete the application:</p>
          <ul class="ml-0 mt-2 list-none sm:ml-8">
            <li><span>Details of the properties being purchased and sold</span></li>
            <li><span>Details of all the applicants and borrowers (individual, company, trust)</span></li>
            <li><span>Income and expense details (if there is any residual debt)</span></li>
          </ul>
        </div>
        <div v-else-if="selectedLoanType === LoanType.SINGLE_SECURITY">
          <p>Below is what you'll need to complete the application:</p>
          <ul class="ml-0 mt-2 list-none sm:ml-8">
            <li><span>Details of the amount of funds required and the reason</span></li>
            <li><span>Details of the property being sold</span></li>
            <li><span>Details of all the applicants and borrowers (individual, company, trust)</span></li>
          </ul>
        </div>
        <div v-else-if="selectedLoanType === LoanType.EQUITY_RELEASE">
          <p>Great, below is what you'll need to complete the application:</p>
          <ul class="ml-0 mt-2 list-none sm:ml-8">
            <li><span>Details of the property you are selling</span></li>
            <li><span>Details of all the applicants</span></li>
            <li><span>The loan amount required and how you will use the funds</span></li>
            <li>
              <span>
                To be eligible you must be selling an existing property and the funds can only be used for a deposit or
                towards the purchase of a new home (for example, the purchase of a retirement home, community home or
                land lease).
              </span>
            </li>
          </ul>
        </div>
      </Transition>
    </div>
    <div class="mt-6">
      <BxButton
        id="application-start"
        variant="primary"
        :disabled="!selectedLoanType"
        :loading="isLoading"
        @click="startApplication"
        >Start application</BxButton
      >
    </div>
  </div>
</template>

<style lang="scss" scoped>
ul {
  li {
    @apply mt-2;

    &::before {
      @apply text-bridgit-vibrantGreen;
      @apply mr-[-1.125rem] rounded-full font-black;
      content: '✓';

      padding: 2px 4px;

      font-size: 10px;

      background-color: #eeecec;
    }
  }

  span {
    @apply relative  left-8 top-[2px] mr-8 inline-flex;
  }
}

.radio-option {
  flex-basis: 100%;
  @screen sm {
    flex-basis: 480px;
  }
}
</style>
